import {useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {styled} from '@mui/material/styles';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {NOT_INTERESTED_REASONS} from '@jobseeker/common-core/constants/notInterested';
import type {NotInterestedReason} from '@jobseeker/common-core/constants/notInterested';

export interface NotInterestedPanelProps {
  title?: string;
  askReasons: boolean;
  onUndo: () => void;
  onSubmit: (reasons: NotInterestedReason[]) => void;
}

const TextButton = styled('button')(({theme}) => ({
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 500,
  fontSize: theme.typography.pxToRem(14),
  lineHeight: theme.typography.pxToRem(21),
  color: theme.palette.primary.main,
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(24),
  },
  ':hover, :focus-visible': {textDecoration: 'underline'},
  ':disabled': {
    cursor: 'default',
    color: theme.palette.text.disabled,
    textDecoration: 'none',
  },
}));

const ReasonChip = styled('button')(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '999px',
  border: '1px solid #7E7E7E33',
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: theme.typography.pxToRem(12),
  lineHeight: theme.typography.pxToRem(18),
  '&[aria-pressed="true"]': {
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(21),
  },
}));

const NotInterestedPanel = ({
  title,
  askReasons,
  onUndo,
  onSubmit,
}: NotInterestedPanelProps) => {
  const isLarge = useIsLargeDevice();
  const [reasons, setReasons] = useState<NotInterestedReason[]>([]);

  const toggleReason = (key: NotInterestedReason) =>
    setReasons(prev =>
      prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]
    );

  const submitButton = (
    <TextButton
      type="button"
      data-testid="not_interested_submit"
      disabled={!reasons.length}
      onClick={() => onSubmit(reasons)}
      sx={{ml: {md: 1}, mt: {xxs: 1.5, md: 0}}}
    >
      Submit feedback
    </TextButton>
  );

  return (
    <Box
      data-component="not_interested_panel"
      sx={{
        bgcolor: {xxs: 'background.paper', md: 'grey.50'},
        borderStyle: 'solid',
        borderWidth: {xxs: 1, md: '0 0 1px'},
        borderColor: 'border.lightGray',
        borderRadius: {xxs: 4, md: 0},
        m: {xxs: 1.5, md: 0},
        p: {xxs: 2.5, md: 2},
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Box display="flex" flexDirection="column" minWidth={0} pb={{md: 0.5}}>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#0E0F0C',
              fontSize: {xxs: '16px', md: '14px'},
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            Job removed from feed
          </Typography>

          {isLarge && title && (
            <Typography
              variant="subtitle2"
              sx={{
                color: '#7E7E7E',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '16px',
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        <TextButton
          type="button"
          data-testid="not_interested_panel_undo"
          onClick={onUndo}
          sx={{flexShrink: 0}}
        >
          Undo
        </TextButton>
      </Box>
      {askReasons && (
        <>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mt={{xxs: 0.75, md: 0.5}}
          >
            <Typography
              variant="caption"
              component="span"
              sx={{
                color: '#0E0F0C',
                fontSize: {xxs: '14px', md: '13px'},
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                whiteSpace: 'nowrap',
              }}
            >
              Why wasn’t this job a fit?
            </Typography>
            <Box
              role="group"
              aria-label="Reasons"
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              gap={1}
            >
              {NOT_INTERESTED_REASONS.map(({key, label}) => {
                const selected = reasons.includes(key);
                return (
                  <ReasonChip
                    key={key}
                    type="button"
                    aria-pressed={selected}
                    data-testid={`not_interested_reason_${key}`}
                    onClick={() => toggleReason(key)}
                  >
                    {label}
                    {selected && <span aria-hidden>×</span>}
                  </ReasonChip>
                );
              })}
              {isLarge && submitButton}
            </Box>
          </Box>
          {!isLarge && <Box>{submitButton}</Box>}
        </>
      )}
    </Box>
  );
};

export default NotInterestedPanel;
